import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Box, Button, FormControl, FormHelperText, Grid, TextField, Typography, Select, MenuItem } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from "@iconify/react";
import { cropsCreateUpdate, cropsGetById } from '../crops.service';
import Loader from '../../../components/loader';
import AlertToast from '../../../components/alertToast';
import {
  FieldLabel,
  formFieldSx,
  helperTextSx,
  sectionTitleSx,
  createMenuProps,
} from '../../../styles/formStyles';
import { primaryIconColor } from '../../../styles/iconStyles';
import {
  createEditBackButtonSx,
  createEditCancelButtonSx,
  createEditCardSx,
  createEditFooterSx,
  createEditPageHeaderSx,
  createEditPageShellSx,
  createEditPageTitleSx,
  createEditPrimaryButtonSx,
} from '../../../styles/pageFormStyles';

const fieldSx = formFieldSx;
const MenuProps = createMenuProps();

const CROP_CATEGORIES = [
  { value: 'CEREAL', label: 'Cereal' },
  { value: 'VEGETABLE', label: 'Vegetable' },
  { value: 'FRUIT', label: 'Fruit' },
  { value: 'CASH_CROP', label: 'Cash Crop' },
];

function AddCrops() {
  const location = useLocation();
  const { id } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const type = queryParams.get("type");
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState("");

  const ALERT_DURATION = 2000;
  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  }

  const cropSchema = z.object({
    name: z.string().min(1, { message: "Crop name is required" }),
    description: z.string().optional().nullable(),
    category: z.string().min(1, { message: "Category is required" }),
  });

  const { handleSubmit, formState: { errors }, control, setValue, setError } = useForm({
    resolver: zodResolver(cropSchema),
    mode: "onChange"
  });

  const fetchData = useCallback(async () => {
    if (editMode === true) {
      const response = await cropsGetById(id);
      if (response?.data) {
        setValue('name', response.data.name || '');
        setValue('description', response.data.description || '');
        setValue('category', response.data.category || '');
      }
    }
  }, [editMode, id, setValue]);

  useEffect(() => {
    if (type === 'edit') {
      setEditMode(true);
    }
  }, [type]);

  useEffect(() => {
    if (type === 'edit') {
      fetchData();
    }
  }, [type, fetchData]);

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      const response = await cropsCreateUpdate(data, editMode, id);

      if (response?.success === false) {
        setResponseMsg(response?.message || "Failed to save crop");
        setAlert('false');
        setOpenAlert(true);
        if (response?.message?.toLowerCase().includes('already exists') || response?.message?.toLowerCase().includes('duplicate')) {
          setError('name', { type: 'manual', message: response?.message });
        }
      } else if (response?.id) {
        setResponseMsg(editMode ? "Crop updated successfully" : "Crop created successfully");
        setAlert('true');
        setOpenAlert(true)
        setTimeout(() => {
          navigate('/crops');
          alertClose();
        }, ALERT_DURATION);
      } else {
        setResponseMsg("Failed to save crop");
        setAlert('false');
        setOpenAlert(true)
      }
      setLoading(false)
    } catch (error) {
      console.error(error);
      setResponseMsg("An error occurred");
      setAlert('false');
      setOpenAlert(true)
      setLoading(false)
    }
  };

  const handleBack = () => {
    navigate('/crops');
  };

  return (
    <Box sx={createEditPageShellSx}>
      <Box sx={createEditPageHeaderSx}>
        <Button
          onClick={handleBack}
          startIcon={<Icon icon="mdi:arrow-left" width="20" height="20" />}
          sx={createEditBackButtonSx}
        >
          Back
        </Button>
        <Grid container alignItems={'center'}>
          <Grid size={12}>
            <Typography className="app-page-title" sx={createEditPageTitleSx}>
              {editMode ? 'Update Crop' : 'Add New Crop'}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} className="material-form">
        <Box sx={createEditCardSx}>
          <Typography sx={sectionTitleSx}>Crop Details</Typography>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid size={12}>
              <Box>
                <FieldLabel label="Crop Name" required />
                <FormControl fullWidth error={!!errors.name} sx={helperTextSx}>
                  <Controller
                    name="name"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="crop-name"
                        placeholder="Enter crop name"
                        sx={fieldSx}
                        variant="outlined"
                        error={!!error}
                        helperText={error ? error.message : ''}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            </Grid>

            <Grid size={12}>
              <Box>
                <FieldLabel label="Category" required />
                <FormControl fullWidth error={!!errors.category} sx={helperTextSx}>
                  <Controller
                    name="category"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <Select
                        displayEmpty
                        MenuProps={MenuProps}
                        IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                        sx={fieldSx}
                        id="category-select"
                        {...field}
                        error={!!error}
                      >
                        <MenuItem value="" disabled>Select category</MenuItem>
                        {CROP_CATEGORIES.map((cat) => (
                          <MenuItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {!!errors.category && (<FormHelperText error>{errors.category.message}</FormHelperText>)}
                </FormControl>
              </Box>
            </Grid>

            <Grid size={12}>
              <Box>
                <FieldLabel label="Description" />
                <FormControl fullWidth sx={helperTextSx}>
                  <Controller
                    name="description"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="description"
                        placeholder="Enter description"
                        sx={fieldSx}
                        variant="outlined"
                        multiline
                        rows={4}
                        error={!!error}
                        helperText={error ? error.message : ''}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ ...createEditFooterSx, flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>
              <Button
                onClick={() => navigate('/crops')}
                sx={{ ...createEditCancelButtonSx, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-start' } }}
              >
                Cancel
              </Button>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, ml: { sm: 'auto' } }}>
              <Button
                variant="contained"
                type="submit"
                sx={{ ...createEditPrimaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
              >
                {editMode ? "Update Crop" : "Create Crop"}
              </Button>
            </Box>
          </Box>
        </Box>
      </form>

      {loading && <Loader />}

      <Box textAlign={'end'}>
        <AlertToast open={openAlert && alert === 'true'} onClose={alertClose} message={responseMsg} variant="success" />
        <AlertToast open={openAlert && alert === 'false'} onClose={alertClose} message={responseMsg} variant="error" />
      </Box>
    </Box>
  )
}

export default AddCrops
